import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { App } from '@/app/App';
import { AppWithoutNav } from '@/app/AppWithoutNav';
import { RootRedirect } from './RootRedirect';
import { RegionGuard } from './RegionGuard';
import { Loader } from '@/shared/ui';

const LoginPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.LoginPage }))
);
const EsiaCallbackPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.EsiaCallbackPage }))
);
const RegionPage = lazy(() =>
  import('@/features/region').then((m) => ({ default: m.RegionPage }))
);
const HomePage = lazy(() =>
  import('@/pages/HomePage').then((m) => ({ default: m.HomePage }))
);
const CatalogPage = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.CatalogPage }))
);
const ProductSearchPage = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.ProductSearchPage }))
);
const ProductPharmaciesPage = lazy(() =>
  import('@/features/catalog').then((m) => ({
    default: m.ProductPharmaciesPage,
  }))
);
const PrescriptionListPage = lazy(() =>
  import('@/features/prescriptions').then((m) => ({
    default: m.PrescriptionListPage,
  }))
);
const PrescriptionDetailPage = lazy(() =>
  import('@/features/prescriptions').then((m) => ({
    default: m.PrescriptionDetailPage,
  }))
);
const MyCartsPage = lazy(() =>
  import('@/features/cart').then((m) => ({ default: m.MyCartsPage }))
);
const PharmacyCartPage = lazy(() =>
  import('@/features/cart').then((m) => ({ default: m.PharmacyCartPage }))
);
const CheckoutPage = lazy(() =>
  import('@/features/cart').then((m) => ({ default: m.CheckoutPage }))
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const FaqPage = lazy(() =>
  import('@/features/faq').then((m) => ({ default: m.FaqPage }))
);
const SupportPage = lazy(() =>
  import('@/features/support').then((m) => ({ default: m.SupportPage }))
);
const OrdersPage = lazy(() =>
  import('@/features/orders').then((m) => ({ default: m.OrdersPage }))
);
const OrderDetailPage = lazy(() =>
  import('@/features/orders').then((m) => ({ default: m.OrderDetailPage }))
);
const RelativesPage = lazy(() =>
  import('@/features/relatives').then((m) => ({ default: m.RelativesPage }))
);
const RelativeFormPage = lazy(() =>
  import('@/features/relatives').then((m) => ({
    default: m.RelativeFormPage,
  }))
);
const ErrorPage = lazy(() =>
  import('@/pages/ErrorPage').then((m) => ({ default: m.ErrorPage }))
);

const fallback = <Loader fullscreen />;

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/esia-callback',
    element: (
      <Suspense fallback={fallback}>
        <EsiaCallbackPage />
      </Suspense>
    ),
  },
  {
    path: '/region',
    element: (
      <Suspense fallback={fallback}>
        <RegionPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: 'login',
        element: (
          <Suspense fallback={fallback}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        element: <RegionGuard />,
        children: [
          {
            path: 'home',
            element: (
              <Suspense fallback={fallback}>
                <HomePage />
              </Suspense>
            ),
          },
          {
            path: 'catalog',
            element: (
              <Suspense fallback={fallback}>
                <CatalogPage />
              </Suspense>
            ),
          },
          {
            path: 'catalog/search',
            element: (
              <Suspense fallback={fallback}>
                <ProductSearchPage />
              </Suspense>
            ),
          },
          {
            path: 'prescriptions',
            element: (
              <Suspense fallback={fallback}>
                <PrescriptionListPage />
              </Suspense>
            ),
          },
          {
            path: 'cart',
            element: (
              <Suspense fallback={fallback}>
                <MyCartsPage />
              </Suspense>
            ),
          },
          {
            path: 'profile',
            element: (
              <Suspense fallback={fallback}>
                <ProfilePage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <AppWithoutNav />,
    children: [
      {
        element: <RegionGuard />,
        children: [
          {
            path: 'cart/:pharmacyId',
            element: (
              <Suspense fallback={fallback}>
                <PharmacyCartPage />
              </Suspense>
            ),
          },
          {
            path: 'cart/:pharmacyId/checkout',
            element: (
              <Suspense fallback={fallback}>
                <CheckoutPage />
              </Suspense>
            ),
          },
          {
            path: 'orders',
            element: (
              <Suspense fallback={fallback}>
                <OrdersPage />
              </Suspense>
            ),
          },
          {
            path: 'orders/:id',
            element: (
              <Suspense fallback={fallback}>
                <OrderDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'profile/faq',
            element: (
              <Suspense fallback={fallback}>
                <FaqPage />
              </Suspense>
            ),
          },
          {
            path: 'profile/support',
            element: (
              <Suspense fallback={fallback}>
                <SupportPage />
              </Suspense>
            ),
          },
          {
            path: 'profile/relatives',
            element: (
              <Suspense fallback={fallback}>
                <RelativesPage />
              </Suspense>
            ),
          },
          {
            path: 'profile/relatives/add',
            element: (
              <Suspense fallback={fallback}>
                <RelativeFormPage />
              </Suspense>
            ),
          },
          {
            path: 'profile/relatives/edit',
            element: (
              <Suspense fallback={fallback}>
                <RelativeFormPage />
              </Suspense>
            ),
          },
          {
            path: 'prescriptions/:id',
            element: (
              <Suspense fallback={fallback}>
                <PrescriptionDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'catalog/products/:productId',
            element: (
              <Suspense fallback={fallback}>
                <ProductPharmaciesPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/error',
    element: (
      <Suspense fallback={fallback}>
        <ErrorPage />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
